import React, { useState, useRef, useEffect } from "react";
import { useBlocker, useLocation, useNavigate } from "react-router-dom";

import {
  Button,
  Container,
  ThemeProvider,
  createTheme,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import Stack from "@mui/material/Stack";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// カスタイマイズしたもの
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import { theme } from "../components/Color/color";
import logo from "../images/rakuCADtrace_logo.png";
import trimIn from "../images/trim_in.png";
import {
  formatCurrency,
  getFormattedToday,
  getformatDateFromISOFormat,
  generateUUID,
  getCurrentDateInJST,
  toJSTISOString,
} from "../components/Utils/utils";
// ローディング
import LoadingDialog from "../components/LoadingDialog/LoadingDialog";
// お問い合わせフォーム導線（設計02）
import { ContactFormButton } from "../components/Dashboard/CustomerStatusDisplay";

// redux
import {
  removeEstimation,
  selectCheckedEstimationAmountList,
  setCheckedDeliveryDates,
} from "../features/estimationAmountList/estimationAmountListSlice";
import {
  selectCheckedFailedFiles,
  selectInquirySent,
  markInquirySent,
} from "../features/failedFiles/failedFilesSlice";
import {
  selectManagementProcess,
  managementProcess,
  changeDisplay,
} from "../features/managementProcess/managementProcessSlice";
import { useAppSelector } from "../app/hooks";
import {
  selectCustomerInfo,
  customerInfoState,
  update as updateCustomerInfo,
} from "../features/customerInfo/customerInfoSlice";
import axiosConfig from "../axiosConfig";
import { useDispatch } from "react-redux";
import { setIsCreatedOrder } from "../features/accountInfo/accountInfoSlice";

// 関数などの定義

// 見積フロー内の画面。見積確認画面から「キャンセル」で前の画面へ戻る導線を含むため、
// これらへ遷移するときは見積内容（estimationAmountList）を保持する。
// フロー外（トップ・ダッシュボード・発注完了など）へ出るときのみ破棄する。
const ESTIMATE_FLOW_PATHS = [
  "/estimate-delivery-plan",
  "/input-customer-info",
  "/input-info-confirmation",
  "/estimation-display",
];

// 見積もりアイテムのデータ型
interface Data {
  itemNo: string;
  description: string;
  quantity: string;
  unit: string;
  unitPrice: string;
  amount: string;
  s3EstimateTargetImageFileURL: string;
  deliveryDate: string;
}
//　見積もりアイテムのデータ作成関数
const createItemData = (
  itemNo: string,
  description: string,
  quantity: string,
  unit: string,
  unitPrice: string,
  amount: string,
  s3EstimateTargetImageFileURL: string,
  deliveryDate: string
): Data => {
  return {
    itemNo,
    description,
    quantity,
    unit,
    unitPrice,
    amount,
    s3EstimateTargetImageFileURL,
    deliveryDate,
  };
};

// ゼロパディングのための関数
const zeroPad = (num: number, places: number): string =>
  String(num).padStart(places, "0");

const EstimationDisplay: React.FC = () => {
  // ナビゲーション
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // redux
  const checkedEstimationAmountList = useAppSelector(
    selectCheckedEstimationAmountList
  );
  const checkedFailedFiles = useAppSelector(selectCheckedFailedFiles);
  const inquirySent = useAppSelector(selectInquirySent);
  const customerInfo: customerInfoState = useAppSelector(selectCustomerInfo);
  const accountInfo = useAppSelector((state) => state.accountInfo);
  const managementProcess: managementProcess = useAppSelector(
    selectManagementProcess
  );

  useEffect(() => {
    if (managementProcess.display !== "InputInfoConfirmation") {
      navigate("/");
    }
  }, []);

  // 見積ID(DB上のID)
  const estimationIdRef = useRef<string | null>(null);
  if (estimationIdRef.current === null) {
    estimationIdRef.current = generateUUID();
  }

  // 見積番号
  const [estimationNumber, setEstimationNumber] = useState<string>("");
  const [orderId, setOrderId] = useState<string>("");
  // 小計
  const subtotal = checkedEstimationAmountList.reduce((sum: number, data) => {
    return sum + (data.estimationAmount || 0);
  }, 0);
  // 税金
  const tax = subtotal * 0.1;
  // 合計
  const total = subtotal + tax;
  // テーブルデータ
  const rows: Data[] = checkedEstimationAmountList.map((data, index) => {
    return createItemData(
      zeroPad(index + 1, 4),
      data.estimationFileName || "",
      "1",
      "枚",
      data.estimationAmount?.toString() || "0",
      data.estimationAmount?.toString() || "0",
      data.estimationS3FileName?.toString() || "",
      data.estimationDateTime?.toString() || ""
    );
  });

  // 見積の読み込み
  const postEstimate = async (data_json: any): Promise<string> => {
    let response: any;
    try {
      response = await axiosConfig.post(
        // "https://6o556yrzjc.execute-api.ap-northeast-1.amazonaws.com/stage/create-estimation-number",
        // "https://rakucad-be.splus-software.com.vn/create-estimation-number",
        `${process.env.REACT_APP_BASE_URL}/orders`,
        data_json,
        {
          headers: {
            "Content-Type": "application/json",
            // Accept: "application/json",
          },
        }
      );
    } catch (error) {
      console.log("Error", error);
    }
    return response;
  };

  // ローディング画面の処理
  const [isLoading, setIsLoading] = useState<boolean>(false);
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // 読み込み時に必要な事前処理
        // ここで顧客情報部分のデータを作る
        const customerInfoData = {
          id: estimationIdRef.current,
          email: customerInfo.email,
          pic: customerInfo.pic,
          picRuby: customerInfo.picRuby,
          companyName: customerInfo.companyName,
          companyNameRuby: customerInfo.companyNameRuby,
          postalCode: customerInfo.postalCode,
          address: customerInfo.address,
          address2: customerInfo.address2,
          phoneNumber: customerInfo.phoneNumber,
          mobilePhoneNumber: customerInfo.mobilePhoneNumber,
          projectName: customerInfo.projectName,
          remarks: customerInfo.remarks,
          subtotal: subtotal,
          consumptionTax: tax,
          totalAmount: total,
        };
        // 見積アイテムのデータ配列を作る
        const items = rows.map((item, index) => ({
          id: generateUUID(),
          estimationListId: estimationIdRef.current,
          itemNo: item.itemNo,
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          amount: item.amount,
          s3EstimateTargetImageFileURL: item.s3EstimateTargetImageFileURL,
          deliveryDate: toJSTISOString(new Date(item.deliveryDate)),
          flexSettingId: checkedEstimationAmountList[index]?.flexSettingId ?? null,
        }));
        const data = {
          customerInfoData,
          items,
        };
        // jsonコードに変換
        const data_json = JSON.stringify(data);
        // 見積の送信と見積番号の取得
        const response_data: any = await postEstimate(data_json);
        const createdEstimateNumber = response_data["data"].body.estimation_number;
        const createdOrderId = response_data["data"].body.order_id;
        // 納期は BE がサーバー時刻で算出した保存値を表示する
        dispatch(
          setCheckedDeliveryDates(
            response_data["data"].body.delivery_dates ?? []
          )
        );
        setEstimationNumber(createdEstimateNumber);
        setOrderId(createdOrderId);
        // 作成した注文を Redux にも保持する。保持しないと、キャンセルで前の画面へ
        // 戻って再度この画面へ来たときに注文IDを失い、発注時に空のIDを送ってしまう。
        dispatch(
          updateCustomerInfo({
            orderId: createdOrderId,
            estimateNumber: createdEstimateNumber,
          })
        );
      } catch (error) {
        console.error("Error fetching disabled dates:", error);
      } finally {
        setIsLoading(false);
      }
    };
    const updateOrder = async () => {
      setIsLoading(true);
      // 更新の成否に関わらず発注できるよう、既存の注文情報を先に復元しておく。
      setEstimationNumber(customerInfo.estimateNumber);
      setOrderId(customerInfo.orderId);
      try {
        // 読み込み時に必要な事前処理
        // ここで顧客情報部分のデータを作る
        const customerInfoData = {
          id: estimationIdRef.current,
          email: customerInfo.email,
          pic: customerInfo.pic,
          picRuby: customerInfo.picRuby,
          companyName: customerInfo.companyName,
          companyNameRuby: customerInfo.companyNameRuby,
          postalCode: customerInfo.postalCode,
          address: customerInfo.address,
          address2: customerInfo.address2,
          phoneNumber: customerInfo.phoneNumber,
          mobilePhoneNumber: customerInfo.mobilePhoneNumber,
          projectName: customerInfo.projectName,
          remarks: customerInfo.remarks,
          subtotal: subtotal,
          consumptionTax: tax,
          totalAmount: total,
        };
        // 見積アイテムのデータ配列を作る
        const items = rows.map((item, index) => ({
          // 通常の見積フローでは drawing の ID を持たない（履歴からの再見積時のみ入る）。
          // その場合 BE は明細の更新をスキップし、注文本体の情報のみ更新する。
          id: customerInfo.drawingsId[index] ?? null,
          estimationListId: estimationIdRef.current,
          itemNo: item.itemNo,
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          amount: item.amount,
          s3EstimateTargetImageFileURL: item.s3EstimateTargetImageFileURL,
          deliveryDate: toJSTISOString(new Date(item.deliveryDate)),
          flexSettingId: checkedEstimationAmountList[index]?.flexSettingId ?? null,
        }));
        const data_json = JSON.stringify({
          customerInfoData,
          items,
        });
        const response = await axiosConfig({
          method: "patch",
          url: `/orders/${customerInfo.orderId}`,
          data: data_json,
        });
        dispatch(
          setCheckedDeliveryDates(response.data.body?.delivery_dates ?? [])
        );
      } catch (error) {
        console.error("Error fetching disabled dates:", error);
      } finally {
        setIsLoading(false);
      }
    };
    if (!accountInfo.isCreatedOrder) {
      fetchData();
      dispatch(setIsCreatedOrder(true));
    }
    if (customerInfo.orderId !== "") {
      updateOrder();
    }
  }, []);
  useBlocker((tx) => {
    // 見積フロー内への遷移（キャンセルで前の画面へ戻る場合）では見積内容を保持する。
    // 以前はフロー内へ戻る場合も破棄していたため、納期の選択画面まで戻ると
    // 見積可能な項目が消え、見積不可の図面が無い場合はトップへ戻されていた。
    if (!ESTIMATE_FLOW_PATHS.includes(tx.nextLocation.pathname)) {
      dispatch(removeEstimation());
      dispatch(changeDisplay("Home"));
    }
    return false;
  });

  // 出力したい見積もり要素を参照するためのref
  const contentRef = useRef<HTMLDivElement>(null);

  // 見積不可ファイルの問い合わせ通知メール（見積時トリガー）
  // 実際の送信可否（通知タイミング設定・24時間重複抑制）は BE 側で判定する。
  // BE レスポンスの inquiry_mail_sent が true（＝実際にメール送信された）場合のみ
  // 画面内二重送信防止フラグ inquirySent を立てる。
  // デフォルト設定(order)では見積時トリガーは BE でスキップされ inquiry_mail_sent=false と
  // なるため、inquirySent は立たず、発注時トリガーの同梱ガードを潰さない。
  const sendInquiryMailOnEstimate = async () => {
    if (inquirySent) return;
    if (checkedFailedFiles.length === 0) return;
    try {
      const response = await axiosConfig({
        method: "post",
        url: "/send-notification-mail",
        data: {
          trigger: "estimate",
          inquiry_items: checkedFailedFiles.map((f) => ({
            file_name: f.fileName,
            s3_file_name: f.s3FileName,
          })),
          customer_info: {
            email: customerInfo.email,
            company_name: customerInfo.companyName,
            pic: customerInfo.pic,
            phone_number: customerInfo.phoneNumber,
            project_name: customerInfo.projectName,
            estimation_number: estimationNumber,
          },
        },
      });
      if (response?.data?.body?.inquiry_mail_sent === true) {
        dispatch(markInquirySent());
      }
    } catch (e) {
      console.log("問い合わせ通知メール送信エラー", e);
    }
  };

  // 見積時トリガー通知の送信済み管理。受動的な画面表示（初回見積取得・既存見積再表示）
  // では毎回送信させず、画面ライフサイクルで一度だけ送る（無駄な送信リクエストを抑制）。
  const estimateInquirySentRef = useRef<boolean>(false);

  // 見積番号確定後に見積時トリガーで通知を送るフック。
  // 送信可否は BE 側の通知タイミング設定で判定される。ここでは毎回の画面表示での
  // 無条件送信を避けるため、コンポーネント表示中は一度だけ発火させる。
  useEffect(() => {
    if (!estimationNumber) return;
    if (checkedFailedFiles.length === 0) return;
    if (estimateInquirySentRef.current) return;
    estimateInquirySentRef.current = true;
    sendInquiryMailOnEstimate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimationNumber]);

  // PDF生成関数
  const generatePDF = async () => {
    if (contentRef.current) {
      // 高解像度でキャプチャするためのスケールを設定
      const scale = 2; // スケール値を適宜調整
      // html2canvasを使用して指定要素をキャプチャ
      const canvas = await html2canvas(contentRef.current, {
        scale: scale,
        useCORS: true, // Cross-origin リソースをキャプチャする場合
      });
      const imgData = canvas.toDataURL("image/png");

      // A4サイズのPDFを作成 (210 x 297 mm)
      const pdf = new jsPDF({
        orientation: "portrait", // 'portrait' or 'landscape'
        unit: "mm",
        format: "a4",
      });

      // 画像のプロパティを取得
      const imgProps = pdf.getImageProperties(imgData);

      // PDFのサイズを設定
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      // キャプチャした内容をPDFに追加
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      const fileName = `${getCurrentDateInJST()}_${estimationNumber}_rakuCAD.pdf`;
      pdf.save(fileName);
      // updateStatusDownload(estimationNumber);
      // 見積不可通知は PDF ダウンロード時には送信しない（設計03）。
      // 通知は見積確定フック／発注確定時にのみ trigger 付きで送る。
    }
  };

  // 見積もり表示テーマ
  const estimationTheme = createTheme({
    typography: {
      // h1: {
      //   fontSize: '2.5rem',
      //   fontWeight: 700,
      // },
      h2: {
        fontSize: "2.5rem",
        // fontWeight: 600,
      },
      body1: {
        fontSize: "1rem",
      },
      body2: {
        fontSize: "0.875rem",
      },
      // 他のスタイルも追加可能
    },
  });

  //////////////////////////////////////////////////
  // 発注処理
  //////////////////////////////////////////////////

  // 発注登録API
  const postPlaceAnOrder = async (data_json: any): Promise<string> => {
    const response = await axiosConfig.post(
      `${process.env.REACT_APP_BASE_URL}/orders-confirmation`,
      data_json,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  };

  //メール送信API
  const postSendNotificationMail = async (data_json: any): Promise<any> => {
    const response = await axiosConfig({
      method: "post",
      url: "/send-notification-mail",
      data: JSON.parse(data_json),
    });
    return response.data.body;
  };

  const [open, setOpen] = useState(false);
  const [capacityErrorOpen, setCapacityErrorOpen] = useState(false);
  const [capacityErrorMessage, setCapacityErrorMessage] = useState("");
  // 顧客承認制（設計04）: 未承認顧客の発注確定ブロック用ダイアログ
  const [notApprovedOpen, setNotApprovedOpen] = useState(false);
  // 発注ポップアップ起動
  const placeAnOrder = () => {
    setOpen(true);
  };
  // ダイアログを閉じる関数
  const handleClose = () => {
    setOpen(false);
  };
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const placeAnOrderConfirm = async () => {
    setOpen(false);
    // ローディング画面を表示
    setIsProcessing(true);
    try {
      //　ここでオーダーを作成しAPIをコール
      const order = {
        id: orderId,
      };
      // jsonコードに変換
      const order_json = JSON.stringify(order);
      // 見積の送信と見積番号の取得
      const placeAnOrderResponse: any = await postPlaceAnOrder(order_json);
      const placeAnOrderResponseData: any = placeAnOrderResponse.body;
      if (placeAnOrderResponseData["status"] === "success") {
        console.log("オーダー登録完了");
      } else if (placeAnOrderResponseData["status"] === "error") {
        console.log("オーダー登録失敗");
      }
      // postデータオブジェクトの作成
      const postSendNotificationMailObj: any = {
        items: placeAnOrderResponseData["items"],
        order: placeAnOrderResponseData["order"],
      };
      // 見積不可ファイルの問い合わせ対象がある場合は発注時トリガーで同梱。
      // 送信可否（通知タイミング設定・24時間重複抑制）は BE 側で判定する。
      if (!inquirySent && checkedFailedFiles.length > 0) {
        postSendNotificationMailObj.trigger = "order";
        postSendNotificationMailObj.inquiry_items = checkedFailedFiles.map((f) => ({
          file_name: f.fileName,
          s3_file_name: f.s3FileName,
        }));
      }
      // jsonコードに変換
      const postSendNotificationMailJson = JSON.stringify(
        postSendNotificationMailObj
      );
      // メール送信実行（失敗しても発注は完了済みのため完了画面に遷移）
      // BE が実際に問い合わせメールを送信した(inquiry_mail_sent=true)場合のみ
      // 画面内二重送信防止フラグ inquirySent を立てる。
      try {
        const mailResult: any = await postSendNotificationMail(
          postSendNotificationMailJson
        );
        if (mailResult?.inquiry_mail_sent === true) {
          dispatch(markInquirySent());
        }
      } catch (mailError) {
        console.log("メール送信エラー（発注は完了済み）", mailError);
      }
      setIsProcessing(false);
      navigate("/order-acceptance-completion");
    } catch (error: any) {
      console.log("Error", error);
      if (
        error?.response?.status === 403 &&
        error?.response?.data?.body?.code === "CUSTOMER_NOT_APPROVED"
      ) {
        // 承認制有効時、未承認顧客の発注確定はブロック（設計04）
        setNotApprovedOpen(true);
      } else if (error?.response?.status === 409) {
        const message = error?.response?.data?.message ||
          "現在の処理上限に達しています。時間をおいて再度お試しいただくか、お問い合わせください。";
        setCapacityErrorMessage(message);
        setCapacityErrorOpen(true);
      } else {
        alert("エラーが発生しました。時間をおいて再度お試しください。");
      }
      setIsProcessing(false);
    } finally {
      //最終処理
    }
  };

  return (
    <>
      <LoadingDialog isOpen={isLoading} message={"読込中"} />
      <LoadingDialog isOpen={isProcessing} message={"処理中"} />
      <Header pageTitle="見積確認" />
      <Container style={{ marginTop: "16px", marginBottom: "100px" }}>
        <Box
          sx={{
            margin: "20px 200px 10px 200px",
          }}
        >
          <Stack direction="row" spacing={2}>
            <ThemeProvider theme={theme}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                onClick={generatePDF}
              >
                見積ダウンロード
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                fullWidth
                onClick={placeAnOrder}
              >
                発注
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  //ダウンロード処理
                  navigate("/input-info-confirmation");
                }}
              >
                キャンセル
              </Button>
            </ThemeProvider>
          </Stack>
        </Box>
        <ThemeProvider theme={estimationTheme}>
          <Box
            sx={{
              marginTop: "20px",
              width: "100%",
              height: "100%",
              border: "1px solid black", // ボーダーのスタイルを指定
              padding: 0,
            }}
          >
            <div style={{ padding: "30px" }} ref={contentRef}>
              {/* 見積書タイトル */}
              <Box
                sx={{
                  textAlign: "center",
                }}
              >
                <Typography variant="h2" sx={{ margin: "20px 0px" }}>
                  お 見 積 書
                </Typography>
              </Box>

              {/* 分割線 */}
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  height: "10px",
                }}
              >
                <Box sx={{ flex: "0 0 20%", backgroundColor: "#000000" }} />
                <Box sx={{ flex: "0 0 60%", backgroundColor: "#3964B1" }} />
                <Box sx={{ flex: "0 0 20%", backgroundColor: "#000000" }} />
              </Box>

              {/* 情報欄 */}
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  marginTop: "20px",
                  height: "100%",
                }}
              >
                {/* 左ブロック*/}
                <Box
                  sx={{
                    width: "55%",
                    height: "100%",
                    // backgroundColor: "green",
                    textAlign: "center",
                  }}
                >
                  {/* 会社名 */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      height: "100%",
                      // backgroundColor: "aqua",
                      marginTop: "20px",
                    }}
                  >
                    <Box sx={{ flex: "0 0 80%", textAlign: "left" }}>
                      <Typography variant="h5">
                        {customerInfo.companyName}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: "0 0 20%", textAlign: "left" }}>
                      <Typography>御中</Typography>
                    </Box>
                  </Box>
                  {/* 担当者名 */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      height: "100%",
                      // backgroundColor: "aqua",
                    }}
                  >
                    <Box sx={{ flex: "0 0 80%", textAlign: "left" }}>
                      <Typography variant="h5">{customerInfo.pic}</Typography>
                    </Box>
                    <Box sx={{ flex: "0 0 20%", textAlign: "left" }}>
                      <Typography>様</Typography>
                    </Box>
                  </Box>
                  {/* 郵便番号 */}
                  <Box
                    sx={{
                      // backgroundColor: "aqua",
                      marginTop: "20px",
                      textAlign: "left",
                    }}
                  >
                    <Typography>〒{customerInfo.postalCode}</Typography>
                  </Box>
                  {/* 住所 */}
                  <Box
                    sx={{
                      // backgroundColor: "aqua",
                      textAlign: "left",
                    }}
                  >
                    <Typography variant="h6">{customerInfo.address}</Typography>
                  </Box>
                  {/* マンション等 */}
                  <Box
                    sx={{
                      // backgroundColor: "aqua",
                      textAlign: "left",
                    }}
                  >
                    <Typography variant="h6">
                      {customerInfo.address2}
                    </Typography>
                  </Box>
                  {/* 以下の通りお見積り申し上げます。 */}
                  <Box
                    sx={{
                      // backgroundColor: "aqua",
                      marginTop: "20px",
                      textAlign: "left",
                    }}
                  >
                    <Typography>以下の通りお見積り申し上げます。</Typography>
                  </Box>
                  {/* 見積もり金額 */}
                  <Box
                    sx={{
                      display: "flex",
                      // backgroundColor: "aqua",
                    }}
                  >
                    <Box
                      sx={{
                        flex: "0 0 35%",
                        backgroundColor: "#000000",
                        padding: "14px",
                      }}
                    >
                      <Typography color={"#ffffff"}>
                        お見積り金額（税込）
                      </Typography>
                    </Box>
                    <Box sx={{ flex: "0 0 5%", backgroundColor: "#3964B1" }}>
                      <Typography
                        variant="h4"
                        color={"#ffffff"}
                        sx={{ marginTop: "5px" }}
                      >
                        ￥
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        flex: "0 0 35%",
                        backgroundColor: "#3964B1",
                        textAlign: "right",
                      }}
                    >
                      <Typography
                        variant="h4"
                        color={"#ffffff"}
                        sx={{ marginTop: "5px" }}
                      >
                        {formatCurrency(total)}
                      </Typography>
                    </Box>
                  </Box>
                  {/* 支払条件 */}
                  <Box
                    sx={{
                      display: "flex",
                      marginTop: "20px",
                      // backgroundColor: "aqua",
                    }}
                  >
                    <Box
                      sx={{
                        flex: "0 0 25%",
                        backgroundColor: "#000000",
                        padding: "14px",
                        borderBottom: "2px solid #ffffff",
                      }}
                    >
                      <Typography color={"#ffffff"}>支払条件</Typography>
                    </Box>
                    <Box
                      sx={{
                        flex: "0 0 51%",
                        backgroundColor: "#ffffff",
                        padding: "14px",
                        borderBottom: "2px solid #000000",
                        textAlign: "left",
                      }}
                    >
                      <Typography color={"#000000"}>
                        月末締め翌月末払い（銀行振込）
                      </Typography>
                    </Box>
                  </Box>
                  {/* 有効期限 */}
                  <Box
                    sx={{
                      display: "flex",
                      // backgroundColor: "aqua",
                    }}
                  >
                    <Box
                      sx={{
                        flex: "0 0 25%",
                        backgroundColor: "#000000",
                        padding: "14px",
                        borderBottom: "2px solid #000000",
                      }}
                    >
                      <Typography color={"#ffffff"}>有効期限</Typography>
                    </Box>
                    <Box
                      sx={{
                        flex: "0 0 51%",
                        backgroundColor: "#ffffff",
                        padding: "14px",
                        borderBottom: "2px solid #000000",
                        textAlign: "left",
                      }}
                    >
                      <Typography color={"#000000"}>発行より1ヶ月</Typography>
                    </Box>
                  </Box>
                </Box>
                {/* 右ブロック*/}
                <Box sx={{ width: "50%", height: "100%" }}>
                  {/* 見積日 */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "right",
                      alignItems: "baseline",
                      // backgroundColor: "aqua",
                      marginTop: "12px",
                    }}
                  >
                    <Box sx={{ flex: "0 0 15%", textAlign: "left" }}>
                      <Typography>見積日</Typography>
                    </Box>
                    <Box sx={{ flex: "0 0 50%", textAlign: "center" }}>
                      <Typography>{getFormattedToday()}</Typography>
                    </Box>
                  </Box>
                  {/* 見積番号 */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "right",
                      alignItems: "baseline",
                      // backgroundColor: "aqua",
                    }}
                  >
                    <Box sx={{ flex: "0 0 15%", textAlign: "left" }}>
                      <Typography>見積番号</Typography>
                    </Box>
                    <Box sx={{ flex: "0 0 50%", textAlign: "center" }}>
                      <Typography>{estimationNumber}</Typography>
                    </Box>
                  </Box>
                  {/* 登録番号 */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "right",
                      alignItems: "baseline",
                      // backgroundColor: "aqua",
                    }}
                  >
                    <Box sx={{ flex: "0 0 15%", textAlign: "left" }}>
                      <Typography>登録番号</Typography>
                    </Box>
                    <Box sx={{ flex: "0 0 50%", textAlign: "center" }}>
                      <Typography>T7-1800-0111-6804</Typography>
                    </Box>
                  </Box>
                  {/* ロゴエリア */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "right",
                      marginTop: "102px",
                    }}
                  >
                    <Box sx={{ flex: "0 0 65%" }}>
                      {/* ロゴ画像 */}
                      <Box
                        sx={
                          {
                            // backgroundColor: "aqua",
                          }
                        }
                      >
                        <img
                          src={logo}
                          alt="raku cad trace"
                          style={{
                            height: 80,
                            marginLeft: 0,
                            marginBottom: 0,
                          }}
                        />
                      </Box>
                      {/* 運営会社 */}
                      {/* <Box
                        sx={{
                          // backgroundColor: "aqua",
                          textAlign: "center",
                        }}
                      >
                        <Typography>(運営会社：株式会社トリム)</Typography>
                      </Box> */}
                    </Box>
                    {/* 捺印 */}
                    <Box sx={{ flex: "0 0 20%" }}>
                      <img src={trimIn} alt="trim in" style={{ height: 120 }} />
                    </Box>
                  </Box>
                  {/* 会社名 */}
                  <Box
                    sx={{
                      // backgroundColor: "aqua",
                      marginTop: "20px",
                      marginLeft: "120px",
                    }}
                  >
                    <Typography>株式会社トリム</Typography>
                  </Box>
                  {/* 郵便番号（自社） */}
                  <Box
                    sx={{
                      // backgroundColor: "aqua",
                      marginLeft: "120px",
                    }}
                  >
                    <Typography>〒464-0085</Typography>
                  </Box>
                  {/* 住所（自社） */}
                  <Box
                    sx={{
                      // backgroundColor: "aqua",
                      marginLeft: "120px",
                    }}
                  >
                    <Typography>
                      愛知県名古屋市千種区古出来3-2-5 大信ビル2F
                    </Typography>
                  </Box>
                  {/* TEL（自社） */}
                  <Box
                    sx={{
                      // backgroundColor: "aqua",
                      marginTop: "20px",
                      marginLeft: "120px",
                    }}
                  >
                    <Typography>TEL:052-739-6832</Typography>
                  </Box>
                  {/* メールアドレス */}
                  <Box
                    sx={{
                      // backgroundColor: "aqua",
                      marginLeft: "120px",
                    }}
                  >
                    <Typography>rakucad@cad-trace.jp</Typography>
                  </Box>
                  {/* 営業時間 */}
                  <Box
                    sx={{
                      // backgroundColor: "aqua",
                      marginLeft: "120px",
                    }}
                  >
                    <Typography>営業時間／平日 9:00~18:00</Typography>
                  </Box>
                </Box>
              </Box>

              {/* 案件名欄 */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    width: "100%",
                    minHeight: "50px",
                    marginTop: "60px",
                  }}
                >
                  <Box sx={{ 
                    flex: "0 0 15%", 
                    backgroundColor: "#000000",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "50px",
                  }}>
                    <Typography variant="h6" color={"#ffffff"}>
                      案件名
                    </Typography>
                  </Box>
                  <Box
                    sx={{ 
                      flex: "0 0 85%", 
                      borderBottom: "2px solid black",
                      display: "flex",
                      alignItems: "center",
                      minHeight: "50px",
                      padding: "10px 20px",
                    }}
                  >
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        width: "100%",
                      }}
                    >
                      {customerInfo.projectName}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              {/* 備考欄（納期選択画面で入力した内容を印字） */}
              {customerInfo.remarks && (
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      width: "100%",
                      minHeight: "50px",
                      marginTop: "20px",
                    }}
                  >
                    <Box
                      sx={{
                        flex: "0 0 15%",
                        backgroundColor: "#000000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "50px",
                      }}
                    >
                      <Typography variant="h6" color={"#ffffff"}>
                        備考
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        flex: "0 0 85%",
                        borderBottom: "2px solid black",
                        display: "flex",
                        alignItems: "center",
                        minHeight: "50px",
                        padding: "10px 20px",
                      }}
                    >
                      <Typography
                        sx={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          width: "100%",
                        }}
                      >
                        {customerInfo.remarks}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
              {/* 見積もりアイテムテーブル */}
              <Box sx={{ marginTop: "30px" }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          align="center"
                          sx={{ backgroundColor: "#000000", padding: "6px" }}
                        >
                          <Typography color={"#ffffff"}>品番</Typography>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ backgroundColor: "#3964B1", padding: "6px" }}
                        >
                          <Typography color={"#ffffff"}>内容</Typography>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ backgroundColor: "#000000", padding: "6px" }}
                        >
                          <Typography color={"#ffffff"}>納期</Typography>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ backgroundColor: "#3964B1", padding: "6px" }}
                        >
                          <Typography color={"#ffffff"}>数量</Typography>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ backgroundColor: "#000000", padding: "6px" }}
                        >
                          <Typography color={"#ffffff"}>単位</Typography>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ backgroundColor: "#3964B1", padding: "6px" }}
                        >
                          <Typography color={"#ffffff"}>単価(税抜)</Typography>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ backgroundColor: "#000000", padding: "6px" }}
                        >
                          <Typography color={"#ffffff"}>金額(税抜)</Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row, index) => (
                        <TableRow
                          key={row.itemNo}
                          sx={{
                            backgroundColor:
                              index % 2 === 0 ? "white" : "grey.200",
                          }}
                        >
                          <TableCell align="center">{row.itemNo}</TableCell>
                          <TableCell align="center">
                            {row.description}
                          </TableCell>
                          <TableCell align="center">
                            {getformatDateFromISOFormat(row.deliveryDate)}
                          </TableCell>
                          <TableCell align="center">{row.quantity}</TableCell>
                          <TableCell align="center">{row.unit}</TableCell>
                          <TableCell align="center">{row.unitPrice}</TableCell>
                          <TableCell align="center">{row.amount}</TableCell>
                        </TableRow>
                      ))}
                      {checkedFailedFiles.map((failed, idx) => {
                        const inquiryIndex = rows.length + idx;
                        return (
                          <TableRow
                            key={`inq-${failed.s3FileName}`}
                            sx={{
                              backgroundColor: "#fffbe6",
                            }}
                          >
                            <TableCell align="center">
                              {zeroPad(inquiryIndex + 1, 4)}
                            </TableCell>
                            <TableCell align="center">
                              {failed.fileName}
                            </TableCell>
                            <TableCell
                              align="center"
                              colSpan={5}
                              sx={{ color: "#d84315", fontWeight: "bold" }}
                            >
                              本項目は見積対象外となります。お手数ですが、お見積フォームより別途お見積をご依頼ください。
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              {/* ※と小計～合計 */}
              <Box sx={{ display: "flex" }}>
                {/* ※ */}
                <Box sx={{ flex: "0 0 60%" }}>
                  <Box>
                    <Typography>
                      ※追加修正は別途料金が発生致します。詳細はお問合せください。
                    </Typography>
                    <Typography>
                      ※内容等変更が生じる場合は再見積もり致します。
                    </Typography>
                  </Box>
                </Box>
                {/* 小計-合計 */}
                <Box sx={{ flex: "0 0 40%" }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "right",
                      // backgroundColor: "aqua",
                    }}
                  >
                    <Box
                      sx={{
                        flex: "0 0 30%",
                        backgroundColor: "#000000",
                        padding: "14px",
                        borderBottom: "2px solid #ffffff",
                      }}
                    >
                      <Typography
                        color={"#ffffff"}
                        sx={{ textAlign: "center" }}
                      >
                        小計
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        flex: "0 0 41%",
                        backgroundColor: "#ffffff",
                        padding: "14px",
                        borderBottom: "2px solid #000000",
                        textAlign: "left",
                      }}
                    >
                      <Typography
                        color={"#000000"}
                        sx={{ textAlign: "center" }}
                      >
                        {formatCurrency(subtotal)}
                      </Typography>
                    </Box>
                  </Box>
                  {/* 消費税 */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "right",
                      // backgroundColor: "aqua",
                    }}
                  >
                    <Box
                      sx={{
                        flex: "0 0 30%",
                        backgroundColor: "#000000",
                        padding: "14px",
                        borderBottom: "2px solid #ffffff",
                      }}
                    >
                      <Typography
                        color={"#ffffff"}
                        sx={{ textAlign: "center" }}
                      >
                        消費税
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        flex: "0 0 41%",
                        backgroundColor: "#ffffff",
                        padding: "14px",
                        borderBottom: "2px solid #000000",
                        textAlign: "left",
                      }}
                    >
                      <Typography
                        color={"#000000"}
                        sx={{ textAlign: "center" }}
                      >
                        {formatCurrency(tax)}
                      </Typography>
                    </Box>
                  </Box>
                  {/* 合計 */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "right",
                      // backgroundColor: "aqua",
                    }}
                  >
                    <Box
                      sx={{
                        flex: "0 0 30%",
                        backgroundColor: "#000000",
                        padding: "14px",
                        borderBottom: "2px solid #000000",
                      }}
                    >
                      <Typography
                        color={"#ffffff"}
                        sx={{ textAlign: "center" }}
                      >
                        合計
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        flex: "0 0 41%",
                        backgroundColor: "#ffffff",
                        padding: "14px",
                        borderBottom: "2px solid #000000",
                        textAlign: "left",
                      }}
                    >
                      <Typography
                        color={"#000000"}
                        sx={{ textAlign: "center" }}
                      >
                        {formatCurrency(total)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* 但し書き */}
              <Box
                sx={{
                  height: "200px",
                  backgroundColor: "#E1E1E1",
                  padding: "20px",
                  marginTop: "20px",
                }}
              >
                <Typography>
                  本見積もり書は、図面トレースの金額です。
                </Typography>
                <Typography>付帯業務などは別途お見積りいたします。</Typography>
                <Typography>
                  詳細確認のため、メールまたは電話にて連絡する場合があります。
                </Typography>
                <Typography>
                  rakuCADtraceは株式会社トリムが運営しています。
                </Typography>
                <Typography>
                  ご不明な点がございましたらお問い合わせください。
                </Typography>
              </Box>
              {/* rakuCADtraceロゴ */}
              <Box sx={{ textAlign: "center", marginTop: "20px" }}>
                <Box
                  sx={
                    {
                      // backgroundColor: "aqua",
                    }
                  }
                >
                  <img
                    src={logo}
                    alt="raku cad trace"
                    style={{
                      height: 60,
                      marginLeft: "auto",
                      marginRight: "auto",
                      marginBottom: 0,
                    }}
                  />
                </Box>
              </Box>
              {/* 運営会社 */}
              {/* <Box sx={{ textAlign: "center" }}>
                <Typography>（運営会社：株式会社トリム）</Typography>
              </Box> */}
            </div>
          </Box>
        </ThemeProvider>
        <Box>
          <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="order-confirmation-dialog-title"
            aria-describedby="order-confirmation-dialog-description"
          >
            <DialogTitle id="order-confirmation-dialog-title">
              最終確認
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="order-confirmation-dialog-description">
                <Typography component="span">
                  発注内容にお間違いありませんか？
                </Typography>
                <Typography component="span">
                  『発注確定』を押すと発注が確定します。
                </Typography>
              </DialogContentText>
              {checkedFailedFiles.length > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    backgroundColor: "#fff8e1",
                    borderLeft: "4px solid #ff9800",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
                    {checkedFailedFiles.map((f) => f.fileName).join("、")}
                  </Typography>
                  <Typography variant="body2">
                    上記画像は詳細確認が必要なため、お見積りできませんでした。
                    発注確定後も、上記図面の見積金額は自動算出されず、お見積り依頼も行われません。
                    お見積りをご希望の場合は、お手数ですがお見積フォームより別途お見積りをご依頼ください。
                  </Typography>
                  <Button
                    href="https://cadtrace.jp/form/"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    size="small"
                    sx={{ mt: 1 }}
                  >
                    お見積フォームを開く
                  </Button>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={placeAnOrderConfirm} color="primary" autoFocus>
                発注確定
              </Button>
              <Button onClick={handleClose} color="secondary">
                キャンセル
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={capacityErrorOpen}
            onClose={() => setCapacityErrorOpen(false)}
            aria-labelledby="capacity-error-dialog-title"
          >
            <DialogTitle id="capacity-error-dialog-title">
              受入可能枚数の超過
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                {capacityErrorMessage}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                variant="contained"
                color="primary"
                onClick={() => window.open("https://trim.nagoya/information/", "_blank")}
              >
                お問い合わせ
              </Button>
              <Button onClick={() => setCapacityErrorOpen(false)} color="secondary">
                閉じる
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={notApprovedOpen}
            onClose={() => setNotApprovedOpen(false)}
            aria-labelledby="not-approved-dialog-title"
          >
            <DialogTitle id="not-approved-dialog-title">
              アカウント確認中
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                アカウントの確認が完了していないため、発注いただけません。恐れ入りますが、確認完了までお待ちいただくか、お問い合わせフォームよりご連絡ください。
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <ContactFormButton size="medium" />
              <Button onClick={() => setNotApprovedOpen(false)} color="secondary">
                閉じる
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Container>
      <Footer />
    </>
  );
};

export default EstimationDisplay;
